import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, Sparkles, ArrowRight, Mail, Shield, Globe2 } from "lucide-react";
import { ClinqLogo } from "../components/ClinqLogo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Map ISO-2 country codes to a default display currency.
const COUNTRY_TO_CCY = {
  NG: "NGN", KE: "KES", GH: "GHS", ZA: "ZAR",
  GB: "GBP", IE: "EUR", DE: "EUR", FR: "EUR", ES: "EUR",
  IT: "EUR", NL: "EUR", PT: "EUR", BE: "EUR", AT: "EUR",
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [fx, setFx] = useState(null);            // {base, rates, currencies, …}
  const [ccy, setCcy] = useState(() =>
    (typeof window !== "undefined" && window.localStorage.getItem("clinq_ccy")) || "USD"
  );
  const nav = useNavigate();

  // Load plans + rates in parallel.
  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND}/api/billing/public-plans`).then((r) => r.data).catch(() => []),
      axios.get(`${BACKEND}/api/billing/exchange-rates`).then((r) => r.data).catch(() => null),
    ]).then(([p, f]) => { setPlans(p); setFx(f); });
  }, []);

  // Geo-detect the visitor's country and switch to the local currency on
  // first visit (only if they haven't already picked one manually).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem("clinq_ccy")) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get("https://ipapi.co/json/", { timeout: 5000 });
        if (cancelled) return;
        const local = COUNTRY_TO_CCY[(data?.country_code || "").toUpperCase()];
        if (local) setCcy(local);
      } catch (err) { if (process.env.NODE_ENV === "development") console.warn("[pricing] geo-detect failed:", err); }
    })();
    return () => { cancelled = true; };
  }, []);

  const onPickCcy = (c) => {
    setCcy(c);
    try { window.localStorage.setItem("clinq_ccy", c); }
    catch (err) { if (process.env.NODE_ENV === "development") console.warn("[pricing] could not persist currency:", err); }
  };

  const fmt = (usdAmount) => {
    if (!fx?.rates || !fx.rates[ccy]) return `$${(usdAmount || 0).toLocaleString()}`;
    const rate = fx.rates[ccy];
    const meta = (fx.currencies || []).find((x) => x.code === ccy);
    const sym = meta?.symbol || ccy + " ";
    const converted = usdAmount * rate;
    // For ≥1000, no decimals. For <1000 (e.g. EUR/GBP), 0 decimals too.
    const display = Math.round(converted).toLocaleString();
    return `${sym}${display}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-neutral-950 to-neutral-900 text-neutral-100">
      <header className="border-b border-neutral-900 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" data-testid="pricing-back-home">
            <ClinqLogo />
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <Link to="/login" data-testid="pricing-login-link"
                  className="rounded-lg border border-neutral-800 bg-neutral-900 px-4 py-2 font-semibold text-neutral-200 hover:bg-neutral-800">
              Sign in
            </Link>
            <Link to="/signup" data-testid="pricing-signup-link"
                  className="rounded-lg bg-[#F59E0B] px-4 py-2 font-bold text-neutral-950 hover:bg-[#EAB308]">
              Start free trial
            </Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16">
        <section className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#F59E0B]">
            For institutional funders
          </p>
          <h1 className="mt-3 text-4xl font-extrabold sm:text-5xl lg:text-6xl">
            Pricing built for the way you deploy capital.
          </h1>
          <p className="mt-5 text-base text-neutral-400 sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Every funder starts with a <b className="text-[#F59E0B]">90-day free trial</b> of the full
            terminal — verified deal flow, mandate-matched scores, NDA-gated VDR.
            Pick a plan only when you&apos;re convinced.
          </p>
        </section>

        {/* Currency toggle */}
        {fx?.currencies?.length > 0 && (
          <div className="mt-10 flex items-center justify-center gap-3" data-testid="pricing-ccy-toggle">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-400">
              <Globe2 className="h-3.5 w-3.5" /> Show prices in
            </span>
            <div className="flex flex-wrap items-center gap-1 rounded-xl border border-neutral-800 bg-neutral-900 p-1">
              {fx.currencies.map((c) => (
                <button
                  key={c.code}
                  onClick={() => onPickCcy(c.code)}
                  data-testid={`pricing-ccy-${c.code}`}
                  className={
                    "rounded-lg px-3 py-1.5 text-xs font-bold transition-colors " +
                    (ccy === c.code
                      ? "bg-[#F59E0B] text-neutral-950"
                      : "text-neutral-300 hover:bg-neutral-800")
                  }
                  title={c.label}
                >
                  {c.symbol} {c.code}
                </button>
              ))}
            </div>
          </div>
        )}

        <section className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3" data-testid="pricing-plans-grid">
          {plans.map((p) => (
            <div key={p.id} data-testid={`pricing-plan-${p.id}`}
              className={"relative rounded-2xl border bg-neutral-900 p-7 " +
                (p.popular ? "border-[#F59E0B] shadow-[0_30px_60px_-30px_rgba(245,158,11,0.45)] ring-1 ring-[#F59E0B]" : "border-neutral-800")}>
              {p.popular && (
                <span className="absolute -top-3 left-7 inline-flex items-center gap-1 rounded-full bg-[#F59E0B] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-neutral-950">
                  <Sparkles className="h-3 w-3" /> Most popular
                </span>
              )}
              <p className="text-sm font-bold uppercase tracking-wider text-[#F59E0B]">{p.name}</p>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold text-neutral-50" data-testid={`pricing-amount-${p.id}`}>
                  {fmt(p.amount)}
                </span>
                <span className="text-sm text-neutral-500">/ {p.interval}</span>
              </div>
              {ccy !== "USD" && (
                <p className="mt-1 text-xs text-neutral-500" data-testid={`pricing-usd-${p.id}`}>
                  ≈ ${p.amount.toLocaleString()} USD
                </p>
              )}
              <p className="mt-2 text-sm text-neutral-400">{p.blurb}</p>
              <ul className="mt-5 space-y-2.5">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-neutral-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#F59E0B]" />{f}
                  </li>
                ))}
              </ul>
              <button onClick={() => nav("/signup?role=funder")}
                data-testid={`pricing-cta-${p.id}`}
                className={"mt-7 inline-flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-bold transition-colors " +
                  (p.popular ? "bg-[#F59E0B] text-neutral-950 hover:bg-[#EAB308]"
                             : "border border-neutral-700 bg-neutral-950 text-neutral-100 hover:bg-neutral-800")}>
                Start free trial <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ))}
        </section>

        {ccy !== "USD" && fx && (
          <p className="mt-6 text-center text-xs text-neutral-500" data-testid="pricing-fx-note">
            Indicative {ccy} prices · live rate from {fx.source || "FX feed"} · billed in your account&apos;s default currency at checkout.
          </p>
        )}

        <section className="mt-16 rounded-2xl border border-neutral-800 bg-neutral-900/60 p-8 backdrop-blur sm:p-10">
          <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-3">
            <div className="md:col-span-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">Enterprise & DFIs</p>
              <h2 className="mt-2 text-2xl font-bold sm:text-3xl">Need a custom mandate, SLA or API access?</h2>
              <p className="mt-3 text-sm text-neutral-400 sm:text-base">
                For DFIs, multi-fund managers and accelerator networks. Talk to our team
                for a custom plan with dedicated success management, custom benchmarks,
                and white-label options.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a href="mailto:sales@clinq.africa?subject=Clinq%20Enterprise%20Inquiry"
                 data-testid="pricing-talk-to-sales"
                 className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#F59E0B] px-5 py-3 text-sm font-bold text-neutral-950 hover:bg-[#EAB308]">
                <Mail className="h-4 w-4" /> Talk to sales
              </a>
              <Link to="/signup" data-testid="pricing-self-serve"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-neutral-700 bg-neutral-950 px-5 py-3 text-sm font-bold text-neutral-100 hover:bg-neutral-900">
                Or self-serve →
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-4 text-sm text-neutral-400 sm:grid-cols-3" data-testid="pricing-trust-row">
          <Trust icon={Shield} title="NDA-gated VDR" body="Every founder signs a mutual NDA + non-circumvent clause before you see their data." />
          <Trust icon={Check}  title="Verified founders" body="Every business is manually verified by the Clinq admin team. No noise, no scams." />
          <Trust icon={Sparkles} title="AI mandate match" body="Mandate-aware compatibility scoring lets you filter to only the deals that fit your thesis." />
        </section>

        <section className="mt-14 text-center text-xs text-neutral-500">
          <p>Cancel anytime. Founders use Clinq <b className="text-neutral-300">free, forever</b>.</p>
        </section>
      </main>
    </div>
  );
}

function Trust({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900 p-5">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#F59E0B]/10 text-[#F59E0B]">
        <Icon className="h-4 w-4" />
      </span>
      <h3 className="mt-3 text-sm font-bold text-neutral-100">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-neutral-400">{body}</p>
    </div>
  );
}
